import React, { useState, useCallback, useRef, useEffect } from "react";
import { Box, Typography, Grid, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Alert, CircularProgress, Stack } from "@mui/material";
import { DynamicFieldAdvanced } from "../../../../../../components/common/input/DynamicFieldAdvanced";
import { useDispatch, useSelector } from "react-redux";
import { setCompensation, setStepCompletion } from "../../../../../../store/basicDetailsSlice";
import { saveEmployeeCompensation } from "../../../../../../api/employeeApi";
import { useNotification } from "../../../../../../hooks/useNotification";
import { CloudUpload as CloudUploadIcon, CloudDownload as CloudDownloadIcon, CheckCircle } from "@mui/icons-material";
import PaymentsIcon from "@mui/icons-material/Payments";

const CompensationBenefits = ({ onNext, onBack, employeeId }) => {
  const dispatch = useDispatch();
  const { showNotification, Notification } = useNotification();
  const compensation = useSelector((state) => state.basicDetails.compensation) || {};

  const [formData, setFormData] = useState({
    ctc: compensation.ctc || "",
    gross: compensation.gross || "",
    hasExistingPF: compensation.hasExistingPF || "",
    uanNumber: compensation.uanNumber || "",
    esicNumber: compensation.esicNumber || "",
    // flexiBenefits: compensation.flexiBenefits || "",
    // tds: compensation.tds || "",
    needFillDetails: compensation.needFillDetails || "",
    healthCheckDoc: compensation.healthCheckDoc || null,
    declarationOfNonPfDoc: compensation.declarationOfNonPfDoc || null,
    declaration: compensation.declaration || false
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showDependencyDialog, setShowDependencyDialog] = useState(false);
  const [showESICField, setShowESICField] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const nonPfFileInputRef = useRef(null);

  useEffect(() => {
    if (compensation && Object.keys(compensation).length > 0) {
      setFormData(prev => {
        const next = { ...prev };
        let updated = false;
        Object.keys(compensation).forEach(key => {
          if (compensation[key] !== prev[key] && key !== 'healthCheckDoc' && key !== 'declarationOfNonPfDoc') {
            next[key] = compensation[key];
            updated = true;
          }
        });
        if (compensation.healthCheckDoc && !prev.healthCheckDoc) {
          next.healthCheckDoc = compensation.healthCheckDoc;
          updated = true;
        }
        if (compensation.declarationOfNonPfDoc && !prev.declarationOfNonPfDoc) {
          next.declarationOfNonPfDoc = compensation.declarationOfNonPfDoc;
          updated = true;
        }
        return updated ? next : prev;
      });
    }
  }, [compensation]);

  useEffect(() => {
    if (formData.gross) {
      const grossValue = parseFloat(formData.gross) || 0;
      setShowESICField(grossValue < 21000);
    }
  }, [formData.gross]);

  const hasExistingPFOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" }
  ];

  // const flexiBenefitsOptions = [
  //   { value: "option1", label: "Option 1" },
  //   { value: "option2", label: "Option 2" },
  //   { value: "option3", label: "Option 3" }
  // ];

  const handleFieldChange = useCallback((name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update Redux on change
    dispatch(setCompensation({ [name]: value }));

    // Handle gross salary change for dependency popup
    if (name === "gross") {
      const grossValue = parseFloat(value) || 0;
      if (grossValue >= 21000 && !formData.needFillDetails) {
        setShowDependencyDialog(true);
      }
      setShowESICField(grossValue < 21000);
    }

    // Clear errors for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  }, [errors, dispatch, formData.needFillDetails]);

  const handleDependencyResponse = useCallback((needDetails) => {
    setFormData(prev => ({
      ...prev,
      needFillDetails: needDetails
    }));
    dispatch(setCompensation({ needFillDetails: needDetails }));
    setShowDependencyDialog(false);
  }, [dispatch]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Required field validations
    if (!formData.ctc) newErrors.ctc = "CTC is required";
    if (!formData.gross) newErrors.gross = "Gross is required";
    if (!formData.hasExistingPF) newErrors.hasExistingPF = "This field is required";
    if (formData.hasExistingPF === "yes" && !formData.uanNumber) {
      newErrors.uanNumber = "UAN Number is required when PF account exists";
    }
    if (formData.hasExistingPF === "no" && (!formData.declarationOfNonPfDoc || formData.declarationOfNonPfDoc.length === 0)) {
      newErrors.declarationOfNonPfDoc = "Declaration of Non-PF document is required when no existing PF account is selected";
    }
    if (showESICField && !formData.esicNumber) {
      newErrors.esicNumber = "ESIC Number is required for salary less than 21K";
    }
    if (formData.needFillDetails === "no" && !formData.declaration) {
      newErrors.declaration = "You must agree to the declaration";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, showESICField]);

  const handleNext = async () => {
    // Mark all required fields as touched
    const requiredFields = ['ctc', 'gross', 'hasExistingPF'];
    if (formData.hasExistingPF === "yes") {
      requiredFields.push('uanNumber');
    }
    if (formData.hasExistingPF === "no") {
      requiredFields.push('declarationOfNonPfDoc');
    }
    if (showESICField) {
      requiredFields.push('esicNumber');
    }
    if (formData.needFillDetails === "no") {
      requiredFields.push('declaration');
    }

    const touchedFields = {};
    requiredFields.forEach(field => {
      touchedFields[field] = true;
    });
    setTouched(touchedFields);

    const isVal = validateForm();
    if (!isVal) {
      const missingFields = [];
      if (!formData.ctc) missingFields.push("CTC");
      if (!formData.gross) missingFields.push("Gross");
      if (!formData.hasExistingPF) missingFields.push("Existing PF choice");
      if (formData.hasExistingPF === "yes" && !formData.uanNumber) missingFields.push("UAN Number");
      if (formData.hasExistingPF === "no" && (!formData.declarationOfNonPfDoc || formData.declarationOfNonPfDoc.length === 0)) {
        missingFields.push("Declaration of Non-PF document");
      }
      if (showESICField && !formData.esicNumber) missingFields.push("ESIC Number");
      if (formData.needFillDetails === "no" && !formData.declaration) missingFields.push("Declaration Acceptance");

      if (missingFields.length > 0) {
        showNotification(`Please fill or upload the following required fields: ${missingFields.join(", ")}`, "error");
      }
      return;
    }

    if (!employeeId) {
      showNotification("Employee ID is missing", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const submissionData = new FormData();
      // Prepare the data object
      const dataObj = { ...formData };
      delete dataObj.healthCheckDoc; // Files go separately
      delete dataObj.declarationOfNonPfDoc; // Files go separately

      submissionData.append('data', JSON.stringify(dataObj));

      if (formData.healthCheckDoc && formData.healthCheckDoc.length > 0) {
        submissionData.append('healthCheckDoc', formData.healthCheckDoc[0]);
      }
      if (formData.declarationOfNonPfDoc && formData.declarationOfNonPfDoc.length > 0) {
        submissionData.append('declarationOfNonPfDoc', formData.declarationOfNonPfDoc[0]);
      }

      await saveEmployeeCompensation(employeeId, submissionData);
      dispatch(setStepCompletion({ step: 'compensationBenefits', isCompleted: true }));

      // Mark newly uploaded files as existing so they don't get re-uploaded next time
      const updatedFormData = { ...formData };
      if (formData.healthCheckDoc && formData.healthCheckDoc.length > 0) {
        const file = formData.healthCheckDoc[0];
        if (file instanceof File) {
          updatedFormData.healthCheckDoc = [{
            name: file.name,
            url: URL.createObjectURL(file),
            isExisting: true
          }];
        }
      }
      if (formData.declarationOfNonPfDoc && formData.declarationOfNonPfDoc.length > 0) {
        const file = formData.declarationOfNonPfDoc[0];
        if (file instanceof File) {
          updatedFormData.declarationOfNonPfDoc = [{
            name: file.name,
            url: URL.createObjectURL(file),
            isExisting: true
          }];
        }
      }
      dispatch(setCompensation(updatedFormData));
      setFormData(updatedFormData);
      setErrors({});
      setTouched({});

      showNotification("Compensation details saved successfully", "success");

      if (onNext) {
        onNext(formData);
      }
    } catch (error) {
      console.error("Error saving compensation details:", error);
      showNotification(error.message || "Failed to save compensation details", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = useCallback(() => {
    if (onNext) {
      onNext();
    }
  }, [onNext]);

  const handleBack = useCallback(() => {
    onBack();
  }, [onBack]);

  const handleFileChange = useCallback((name, files) => {
    setFormData(prev => ({
      ...prev,
      [name]: files
    }));
  }, []);

  const handleFileRemove = useCallback((name, index) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name]?.filter((_, i) => i !== index) || []
    }));
  }, []);

  const handleFileChoose = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((event) => {
    const files = Array.from(event.target.files);
    handleFileChange("healthCheckDoc", files);
    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  }, [handleFileChange]);

  const handleNonPfFileChoose = useCallback(() => {
    nonPfFileInputRef.current?.click();
  }, []);

  const handleNonPfFileInputChange = useCallback((event) => {
    const files = Array.from(event.target.files);
    handleFileChange("declarationOfNonPfDoc", files);
    event.target.value = '';
  }, [handleFileChange]);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', mb: 3.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: '#F5EEFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <PaymentsIcon sx={{ color: '#7522D1', fontSize: 18 }} />
        </Box>
        Compensation & CTC details
      </Typography>

      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="number"
            label="CTC*"
            name="ctc"
            value={formData.ctc}
            onChange={handleFieldChange}
            required
            placeholder="Enter CTC amount"
            error={touched.ctc && !!errors.ctc}
            errorText={touched.ctc ? errors.ctc : ""}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="number"
            label="Monthly Gross*"
            name="gross"
            value={formData.gross}
            onChange={handleFieldChange}
            required
            placeholder="Enter gross salary (>=21,000 for dependency)"
            error={touched.gross && !!errors.gross}
            errorText={touched.gross ? errors.gross : ""}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="select"
            label="Do you have an existing PF account?*"
            name="hasExistingPF"
            value={formData.hasExistingPF}
            onChange={handleFieldChange}
            options={hasExistingPFOptions}
            required
            error={touched.hasExistingPF && !!errors.hasExistingPF}
            errorText={touched.hasExistingPF ? errors.hasExistingPF : ""}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label={formData.hasExistingPF === "yes" ? "UAN Number*" : "UAN Number"}
            name="uanNumber"
            value={formData.uanNumber}
            onChange={handleFieldChange}
            required={formData.hasExistingPF === "yes"}
            placeholder="Enter UAN number"
            error={touched.uanNumber && !!errors.uanNumber}
            errorText={touched.uanNumber ? errors.uanNumber : ""}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label={showESICField ? "ESIC Number*" : "ESIC Number"}
            name="esicNumber"
            value={formData.esicNumber}
            onChange={handleFieldChange}
            required={showESICField}
            placeholder="Enter ESIC number"
            error={touched.esicNumber && !!errors.esicNumber}
            errorText={touched.esicNumber ? errors.esicNumber : ""}
          />
        </Grid>

        {formData.hasExistingPF === "no" && (
          <Grid item size={{ xs: 12 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                bgcolor: '#F8FAFC',
                borderRadius: '12px',
                border: '1px dashed #E2E8F0',
                mb: 1
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', mb: 1 }}>
                Declaration of Non-PF Attachment <Typography component="span" sx={{ color: '#D32F2F' }}>*</Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Since you do not have an existing PF account, please download the Declaration of Non-PF form, sign it, and upload it here.
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  component="a"
                  href="/Declaration Of Non-PF.pdf"
                  download="Declaration Of Non-PF.pdf"
                  startIcon={<CloudDownloadIcon sx={{ fontSize: 18 }} />}
                  sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
                >
                  Download Declaration Form
                </Button>
              </Stack>

              <input
                type="file"
                ref={nonPfFileInputRef}
                onChange={handleNonPfFileInputChange}
                accept=".pdf,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
              />

              <Box sx={{ display: "flex", gap: 1 }}>
                <DynamicFieldAdvanced
                  type="file"
                  name="declarationOfNonPfDoc"
                  value=""
                  onChange={(name, files) => handleFileChange(name, files)}
                  files={formData.declarationOfNonPfDoc || []}
                  onFileRemove={(index) => handleFileRemove("declarationOfNonPfDoc", index)}
                  onFileChoose={handleNonPfFileChoose}
                  accept=".pdf,.png,.jpg,.jpeg"
                  maxSize="4MB"
                  supportedFormats="jpeg, pdf, png"
                  error={touched.declarationOfNonPfDoc && !!errors.declarationOfNonPfDoc}
                  errorText={touched.declarationOfNonPfDoc ? errors.declarationOfNonPfDoc : ""}
                  sx={{ maxWidth: "fit-content !important" }}
                />
              </Box>
            </Paper>
          </Grid>
        )}

        {/* <Grid item size={{ xs: 12, md: 6 }}>
            <DynamicFieldAdvanced
              type="select"
              label="Flexi Benefits"
              name="flexiBenefits"
              value={formData.flexiBenefits}
              onChange={handleFieldChange}
              options={flexiBenefitsOptions}
            />
          </Grid>

          <Grid item size={{ xs: 12, md: 6 }}>
            <DynamicFieldAdvanced
              type="text"
              label="TDS"
              name="tds"
              value={formData.tds}
              onChange={handleFieldChange}
              placeholder="Enter TDS details"
            />
          </Grid> */}

        <Grid item size={{ xs: 12 }}>
          <Typography variant="body2" sx={{ mb: 1, color: "#4F4937" }}>
            Health Check Document (Required for age 40 and above)
          </Typography>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            accept=".pdf,.png,.jpg,.jpeg"
            style={{ display: 'none' }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <DynamicFieldAdvanced
              type="file"
              name="healthCheckDoc"
              value=""
              onChange={(name, files) => handleFileChange(name, files)}
              files={formData.healthCheckDoc || []}
              onFileRemove={(index) => handleFileRemove("healthCheckDoc", index)}
              onFileChoose={handleFileChoose}
              accept=".pdf,.png,.jpg,.jpeg"
              maxSize="4MB"
              supportedFormats="jpeg, pdf, png"
              sx={{ maxWidth: "fit-content !important" }}
            />
          </Box>
        </Grid>

        {formData.needFillDetails === "no" && (
          <Grid item size={{ xs: 12 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                I hereby declare that I am not interested in adding my family members to the Corporate Mediclaim policy. Further, I declare that the Company shall not be responsible for any Mediclaim or hospitalization expenses pertaining to my family.
              </Typography>
            </Alert>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.declaration}
                  onChange={(e) => handleFieldChange("declaration", e.target.checked)}
                />
              }
              label="I agree to the declaration"
            />
            {touched.declaration && errors.declaration && (
              <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                {errors.declaration}
              </Typography>
            )}
          </Grid>
        )}
      </Grid>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}>
        <Button variant="outlined" onClick={handleBack} sx={{ minWidth: 120 }}>
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={handleSkip} sx={{ minWidth: 120 }} disabled={isSubmitting}>
            Skip for now
          </Button>
          <Button variant="contained" onClick={handleNext} sx={{ minWidth: 120 }} disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (employeeId ? "Update" : "Save & Next")}
          </Button>
        </Box>
      </Box>

      {/* Dependency Dialog */}
      <Dialog open={showDependencyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Family Dependency</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Since your gross salary is &gt;=21,000, would you like to add family members to the Corporate Mediclaim policy?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDependencyResponse("yes")}>
            Yes, I want to add details
          </Button>
          <Button onClick={() => handleDependencyResponse("no")}>
            No, I'm not interested
          </Button>
        </DialogActions>
      </Dialog>
      <Notification />
    </Box>
  );
};

export default CompensationBenefits;